import Countdown from 'react-countdown'
import { Link, useHistory } from 'react-router-dom'
import { useContext, useEffect, useMemo, useState, useRef } from 'react'
import dayjs from 'dayjs'
import calendar from 'dayjs/plugin/calendar'
import textServices from 'services/text-services'
import { dlTrackEvent } from 'components/dataLayer/dataLayer'
import CONFIGS from 'build/Config'
import { shouldShowOnboarding } from 'utilities/onboardingTime'
import Breadcrumb from 'components/breadcrumb'
import Slider from 'react-slick'
import Button from 'components/button'
import OnboardingStep from 'components/onboarding-steps'
import { useTrivia } from '../../contexts/trivia-context'
import { useSession } from '../../contexts/session-context'
import { LoaderContext } from '../../contexts/loader-context'
import {
  DivTriviaList,
  DivTriviaListBorder,
  DivTitleTriviaList,
  H2TitleTriviaList,
  TriviaDescription,
  DivTriviaContainer,
  TriviaTitle,
  TriviaSubtitle,
  TriviaSubtitleSpan,
  TriviaPrize,
  TriviaStock,
  TriviaTime,
  TriviaCost,
  TriviaTitlePage,
  TriviaNotFoundContainer,
  TriviaNotFoundText,
  TriviaTimeNumbers,
  TriviaCostNumber
} from '../../theme/trivias'
import { TriviaItem } from './types'
import CountdownDays from './components/CountdownDays'

export enum TriviaStatusEnum {
  AVAILABLE = 1,
  NOT_AVAILABLE = 2,
  WON = 3,
  LOST = 4
}

const TriviaList = () => {
  const history = useHistory()
  const [state, { getTrivias }] = useTrivia()
  const { setLoad } = useContext(LoaderContext)
  const [{ user, data }] = useSession()

  useEffect(() => {
    if (data) {
      getTrivias()
    }
  }, [data])

  if (!state.trivias) {
    setLoad(true)
  } else {
    setLoad(false)
  }

  dayjs.extend(calendar)

  const avaliableTrivias = useMemo(() => {
    return state?.trivias
      ?.filter((item) => {
        let isActive = true
        const nowDate = dayjs(new Date())
        if (item.validFrom) {
          const validFrom = new Date(item.validFrom as string)
          const date = dayjs(validFrom)

          if (nowDate.isBefore(date)) isActive = false
        }

        if (item.validTo) {
          const validTo = new Date(item.validTo as string)
          const date = dayjs(validTo)
          if (nowDate.isAfter(date)) isActive = false
        }

        if (item.stock <= 1) return false

        // If direct access doesn't show never
        if (item?.directAccess) return false

        // Have Ranking always, must show
        if (item?.withRanking) return true

        // If already plays, doesn't show never
        if (item?.profileTriviaStatus === 3) return false

        // If is active, must show
        return isActive
      })
      ?.sort((a, b) => {
        if (a.profileTriviaStatus === 3 && b.profileTriviaStatus !== 3) return 1
        if (b.profileTriviaStatus === 3 && a.profileTriviaStatus !== 3) return -1

        const today = dayjs()
        const diffA = dayjs(a.validTo).diff(today)
        const diffB = dayjs(b.validTo).diff(today)

        return diffB - diffA
      })
  }, [state.trivias])

  const AvailableTrivias = ({ arrTrivias }: { arrTrivias: TriviaItem[] }) => {
    return (
      <>
        {arrTrivias.map((item) => {
          const hasEnoughPoints = parseInt(user?.totalPoints || '0', 10) >= item.costInPoints
          const isAvailable = item.stock !== 0
          const isUnlimited = isAvailable && item.stock === -1
          let shouldShowCountdown = false
          let isActive = hasEnoughPoints

          let dateCountdown
          let countdownType
          const nowDate = dayjs(new Date())
          if (item.validFrom) {
            const validFrom = new Date(item.validFrom as string)
            const date = dayjs(validFrom)
            if (nowDate <= date) {
              countdownType = textServices.findByText('trivias/list/card/start', {
                es: 'Comienza en',
                en: 'Starts at'
              })
              shouldShowCountdown = true
              dateCountdown = date.toDate()
            }
            if (nowDate < date) isActive = false
          }

          if (item.validTo && !countdownType) {
            const validTo = new Date(item.validTo as string)
            const date = dayjs(validTo)
            if (nowDate <= date) {
              countdownType = textServices.findByText('trivias/list/card/end', {
                es: 'Finaliza en',
                en: 'Ends at'
              })
              shouldShowCountdown = true
              dateCountdown = date.toDate()
            }

            if (nowDate > date) isActive = false
          }

          if (item?.profileTriviaStatus === 3) isActive = false

          if (isAvailable && (false as any) && item.imageUrl !== '') {
            return (
              <Link
                key={`trivia-item-${item.id}`}
                to={item?.withRanking && !isActive ? `/trivia/ranking/${item.id}` : `/trivia/${item.id}`}
                className={`h-full trivias__card-list ${isActive || item?.withRanking ? 'trivias__card-list-active' : 'pointer-events-none'}`}
              >
                <DivTriviaListBorder className={`border-b h-full flex flex-col card-container`} isActive={isActive}>
                  <DivTitleTriviaList
                    className={` flex justify-start items-center card-title-cnt ${isActive ? '' : 'opacity-80'}`}
                  >
                    <H2TitleTriviaList className="uppercase py-4 pl-6 pr-4 nersans text-[15px] card-title-int">
                      {item.interest}
                    </H2TitleTriviaList>
                    <h2 className="uppercase py-4 pl-4 pr-6 nersans text-[20px] card-title-name">{item?.name}</h2>
                  </DivTitleTriviaList>
                  <DivTriviaContainer className="pt-4 flex flex-col justify-between flex-1 card-body">
                    <TriviaDescription className="pb-2 text-center px-6 card-body-subtitle">
                      {item.description}
                    </TriviaDescription>
                    <div className="flex items-center card-body-img">
                      <img src={item.imageUrl} className="ml-5" alt="logo" style={{ width: '58px' }} />
                      <h3 className="text-center uppercase text-2xl font-bold px-6 w-full nersans">{item.prize}</h3>
                      <img src={item.imageUrl} className="mr-5 invisible" alt="logo" style={{ width: '58px' }} />
                    </div>

                    <div className="flex justify-between w-full items-center px-6 mt-4 py-1 card-footer">
                      <div>
                        {shouldShowCountdown && (
                          <p className="raleway font-medium card-footer-time-cnt">
                            {countdownType} <br />{' '}
                            <Countdown className="card-footer-time-countdown" date={dateCountdown} />
                          </p>
                        )}
                      </div>
                      {!isUnlimited && (
                        <p className="raleway font-medium uppercase text-center text-xs card-body-stock">
                          {item.stock}{' '}
                          {textServices.findByText('trivias/list/card/available', {
                            es: 'disponible',
                            en: 'available'
                          })}
                          {item.stock > 1 ? 's' : ''}
                        </p>
                      )}
                      {item.costInPoints > 0 && (
                        <div className="flex justify-center items-center card-footer-cost-cnt">
                          <span className="text-3xl uppercase nersans font-bold card-footer-cost-points">
                            {item.costInPoints}
                          </span>
                          <span className="text-xs uppercase nersans mt-2 ml-2">{POINTS_NAME}</span>
                        </div>
                      )}
                      {item.costInPoints === 0 && (
                        <div className="flex justify-center items-center card-footer-cost-free">
                          {textServices.findByText('trivias/list/card/free', { es: 'GRATIS', en: 'FREE' })}
                        </div>
                      )}
                    </div>
                  </DivTriviaContainer>
                  {!hasEnoughPoints && (
                    <div className="uppercase raleway text-xs text-center pb-4 card-footer-cost-not-found">
                      {textServices.findByText('trivias/list/card/enough', {
                        es: 'no tienes suficientes',
                        en: "you don't have enough"
                      })}{' '}
                      {POINTS_NAME}
                    </div>
                  )}
                </DivTriviaListBorder>
              </Link>
            )
          } else if (isAvailable) {
            return (
              <Link
                key={`trivia-item-${item.id}`}
                to={item?.withRanking && !isActive ? `/trivia/ranking/${item.id}` : `/trivia/${item.id}`}
                className={`h-full trivias__card-list ${isActive || item?.withRanking ? 'trivias__card-list-active' : 'pointer-events-none'}`}
              >
                <DivTriviaListBorder className={`border-b card-container h-full flex flex-col`} isActive={isActive}>
                  <DivTitleTriviaList
                    className={` flex justify-start items-center card-title-cnt ${isActive ? '' : 'opacity-80'}`}
                  >
                    <H2TitleTriviaList className="font-bold uppercase text-lg py-4 pl-6 pr-4 card-title-int">
                      {item.interest}
                    </H2TitleTriviaList>
                    <TriviaTitle className="text-sm py-4 pl-4 pr-6 flex justify-center items-center card-title-name">
                      {item?.name}
                    </TriviaTitle>
                  </DivTitleTriviaList>
                  <DivTriviaContainer className="pt-4 flex flex-col justify-between flex-1 card-body">
                    <TriviaSubtitle className="din pb-2 text-center px-6 card-body-subtitle">
                      {textServices.findByText('trivias/questions1', { es: `Contesta `, en: 'Answer ' })}
                      <TriviaSubtitleSpan className="card-body-subtitle-question">
                        {item.nbrOfQuestions}
                      </TriviaSubtitleSpan>
                      {textServices.findByText('trivias/questions2', {
                        es: ` preguntas sobre el tema y gana:`,
                        en: ` questions on the subject and win:`
                      })}
                    </TriviaSubtitle>
                    <TriviaPrize className="text-center uppercase text-2xl px-6 pb-2 card-body-prize">
                      {item.prize}
                    </TriviaPrize>
                    {!isUnlimited && (
                      <TriviaStock className="din uppercase text-center text-xs card-body-stock">
                        {item.stock}{' '}
                        {textServices.findByText('trivias/list/card/available', {
                          es: 'disponible',
                          en: 'available'
                        })}
                        {item.stock > 1 ? 's' : ''}
                      </TriviaStock>
                    )}
                    <div className="flex justify-between w-full items-end px-6 mt-4 py-1 card-footer">
                      <div className="card-footer-time-cnt">
                        {shouldShowCountdown && (
                          <TriviaTime className="card-footer-time-text">
                            {countdownType}
                            <br />{' '}
                            <TriviaTimeNumbers className="card-footer-time-countdown">
                              <Countdown date={dateCountdown} />
                            </TriviaTimeNumbers>
                          </TriviaTime>
                        )}
                      </div>
                      {item.costInPoints > 0 && (
                        <TriviaCost className="flex justify-center items-center card-footer-cost-cnt">
                          <p>
                            {' '}
                            <TriviaCostNumber className="card-footer-cost-points">
                              {item.costInPoints}
                            </TriviaCostNumber>{' '}
                            {POINTS_NAME}{' '}
                          </p>
                          {/* <span className="text-3xl uppercase nersans font-bold">{item.costInPoints}</span>
                            <span className="text-xs uppercase nersans mt-2 ml-2">{POINTS_NAME}</span> */}
                        </TriviaCost>
                      )}
                      {item.costInPoints === 0 && (
                        <TriviaCost className="flex justify-center items-center card-footer-cost-free">
                          {textServices.findByText('trivias/list/card/free', { es: 'GRATIS', en: 'FREE' })}
                        </TriviaCost>
                      )}
                    </div>
                  </DivTriviaContainer>
                  {!hasEnoughPoints && (
                    <div className="uppercase din text-xs text-center pb-2 pt-2 card-footer-cost-not-found">
                      {textServices.findByText('trivias/list/card/enough', {
                        es: 'no tienes suficientes',
                        en: "you don't have enough"
                      })}{' '}
                      {POINTS_NAME}
                    </div>
                  )}
                </DivTriviaListBorder>
              </Link>
            )
          } else {
            return null
          }
        })}
      </>
    )
  }

  // buttom bottom
  const sliderRef = useRef(null)
  const [currentItem, setCurrentItem] = useState<TriviaItem | null>(
    avaliableTrivias?.length ? avaliableTrivias[0] : null
  )

  const [isActive, setIsActive] = useState(null)

  const getCurrentSlide = () => {
    if (sliderRef.current) {
      const index = sliderRef.current.innerSlider.state.currentSlide
      const hasEnoughPoints = parseInt(user?.totalPoints || '0', 10) >= avaliableTrivias[index].costInPoints
      let isActive = hasEnoughPoints

      let countdownType
      const nowDate = dayjs(new Date())
      if (avaliableTrivias[index].validFrom) {
        const validFrom = new Date(avaliableTrivias[index].validFrom as string)
        const date = dayjs(validFrom)
        if (nowDate < date) isActive = false
      }

      if (avaliableTrivias[index].validTo && !countdownType) {
        const validTo = new Date(avaliableTrivias[index].validTo as string)
        const date = dayjs(validTo)

        if (nowDate > date) isActive = false
      }

      if (avaliableTrivias[index]?.profileTriviaStatus === 3) isActive = false

      setCurrentItem(avaliableTrivias[index])
      setIsActive(isActive)
    }
  }

  useEffect(() => {
    if (avaliableTrivias?.length) {
      setCurrentItem(avaliableTrivias[0])
    }
  }, [avaliableTrivias])

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    afterChange: getCurrentSlide // Se ejecuta después de cambiar de slide
  }

  const v2 = CONFIGS.versionPh === 'v2'

  const [onboarding, setOnboarding] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  const steps = [
    {
      image: `${AZURE_BASE_URL}/como-participar_completa_tu_perfil.webp`,
      title: 'Completa tu perfil',
      subtitle: 'termina de armar tu cuenta con tus datos personales.'
    },
    {
      image: `${AZURE_BASE_URL}/como-participar_responde_la_trivia.webp`,
      title: 'Responde la trivia',
      subtitle: 'y gana oportunidades respondiendo correctamente.'
    },
    {
      image: `${AZURE_BASE_URL}/como-participar_acumula_oportunidades.webp`,
      title: 'Acumula oportunidades',
      subtitle:
        'con tus participaciones y gana un sello. Quien haya acumulado más al finalizar la dinámica, será el ganador.'
    }
  ]

  const toggleOnboarding = () => {
    dlTrackEvent('user_interaction', {
      action: 'click',
      element: onboarding ? 'close onboarding' : 'open onboarding',
      section: 'trivias :: onboarding'
    })
    setOnboarding(!onboarding)
  }

  useEffect(() => {
    if (!hasChecked) {
      const showOnboarding = shouldShowOnboarding('trivias-onboarding');
      setOnboarding(showOnboarding);
      setHasChecked(true);
    }
  }, [hasChecked]);

  if (onboarding && v2) {
    return (
      <OnboardingStep
        title="¿CÓMO PARTICIPAR?"
        subtitle="COMPLETA EL RETO Y GANA"
        close={toggleOnboarding}
        steps={steps}
      />
    )
  }

  const handleOpenTrivia = () => {
    if (currentItem) {
      gtag('event', 'Click', {
        event_category: GTM_EVENT_CATEGORY,
        event_label: `Comenzar trivia :: ${currentItem.name}`,
        value: 'trivias'
      })

      dlTrackEvent('user_interaction', {
        action: 'click',
        element: `Comenzar trivia :: ${currentItem.name}`,
        section: 'trivias'
      })

      history.push((currentItem?.withRanking && !isActive) ? `/trivia/ranking/${currentItem.id}` : `/trivia/${currentItem.id}`)
    }
  }

  return (
    <>
      <DivTriviaList className="trivias__container mx-auto">
        <div className="w-full">
          <div className="trivias__title-cnt relative w-screen flex justify-start items-center px-6 py-4 md:px-0">
            {CONFIGS?.versionPh === 'v2' ? (
              <Breadcrumb text="Trivias" handleClick={() => history.goBack()} />
            ) : (
              <TriviaTitlePage className="text-3xl font-bold uppercase trivias__title">
                {textServices.findByText('trivias/list/title', { es: 'Trivias', en: 'Trivias' })}
              </TriviaTitlePage>
            )}
            <button
              className="din uppercase right-0 top-0 absolute h-18 hidden"
              style={{ fontSize: 8 }}
              onClick={() => {
                gtag('event', 'event_to_ga', { event_category: 'Trivias', event_label: 'Ordenar' })
                dlTrackEvent('user_interaction', { action: 'click', element: 'ordenar por', section: 'trivia' })
              }}
            >
              {textServices.findByText('trivias/list/sort', { es: 'ordenar por', en: 'sort by' })}
            </button>
          </div>
          {state.trivias && avaliableTrivias.length ? (
            CONFIGS.versionPh === 'v2' ? (
              <Slider arrows={true} {...settings} ref={sliderRef}>
                {avaliableTrivias.map((item) => {
                  const hasEnoughPoints = parseInt(user?.totalPoints || '0', 10) >= item.costInPoints
                  const isAvailable = item.stock !== 0
                  const isUnlimited = isAvailable && item.stock === -1
                  let shouldShowCountdown = false
                  let isActive = hasEnoughPoints

                  let dateCountdown
                  let countdownType
                  const nowDate = dayjs(new Date())
                  if (item.validFrom) {
                    const validFrom = new Date(item.validFrom as string)
                    const date = dayjs(validFrom)
                    if (nowDate <= date) {
                      countdownType = textServices.findByText('trivias/list/card/start', {
                        es: 'Comienza en',
                        en: 'Starts at'
                      })
                      shouldShowCountdown = true
                      dateCountdown = date.toDate()
                    }
                    if (nowDate < date) isActive = false
                  }

                  if (item.validTo && !countdownType) {
                    const validTo = new Date(item.validTo as string)
                    const date = dayjs(validTo)
                    if (nowDate <= date) {
                      countdownType = textServices.findByText('trivias/list/card/end', {
                        es: 'Finaliza en',
                        en: 'Ends at'
                      })
                      shouldShowCountdown = true
                      dateCountdown = date.toDate()
                    }

                    if (nowDate > date) isActive = false
                  }

                  if (item?.profileTriviaStatus === 3) isActive = false

                  if (isAvailable && (false as any) && item.imageUrl !== '') {
                    return (
                      <Link
                        key={`trivia-item-${item.id}`}
                        to={item?.withRanking && !isActive ? `/trivia/ranking/${item.id}` : `/trivia/${item.id}`}
                        className={`h-full trivias__card-list ${isActive || item?.withRanking ? 'trivias__card-list-active' : 'pointer-events-none'}`}
                      >
                        <DivTriviaListBorder
                          className={`border-b h-full flex flex-col card-container`}
                          isActive={isActive}
                        >
                          <DivTitleTriviaList
                            className={` flex justify-start items-center card-title-cnt ${isActive ? '' : 'opacity-80'}`}
                          >
                            <H2TitleTriviaList className="uppercase py-4 pl-6 pr-4 nersans text-[15px] card-title-int">
                              {item.interest}
                            </H2TitleTriviaList>
                            <h2 className="uppercase py-4 pl-4 pr-6 nersans text-[20px] card-title-name">
                              {item?.name}
                            </h2>
                          </DivTitleTriviaList>
                          <DivTriviaContainer className="pt-4 flex flex-col justify-between flex-1 card-body">
                            <TriviaDescription className="pb-2 text-center px-6 card-body-subtitle">
                              {item.description}
                            </TriviaDescription>
                            <div className="flex items-center card-body-img">
                              <img src={item.imageUrl} className="ml-5" alt="logo" style={{ width: '58px' }} />
                              <h3 className="text-center uppercase text-2xl font-bold px-6 w-full nersans">
                                {item.prize}
                              </h3>
                              <img
                                src={item.imageUrl}
                                className="mr-5 invisible"
                                alt="logo"
                                style={{ width: '58px' }}
                              />
                            </div>

                            <div className="flex justify-between w-full items-center px-6 mt-4 py-1 card-footer">
                              <div>
                                {shouldShowCountdown && (
                                  <p className="raleway font-medium card-footer-time-cnt">
                                    {countdownType} <br />{' '}
                                    <Countdown className="card-footer-time-countdown" date={dateCountdown} />
                                  </p>
                                )}
                              </div>
                              {!isUnlimited && (
                                <p className="raleway font-medium uppercase text-center text-xs card-body-stock">
                                  {item.stock}{' '}
                                  {textServices.findByText('trivias/list/card/available', {
                                    es: 'disponible',
                                    en: 'available'
                                  })}
                                  {item.stock > 1 ? 's' : ''}
                                </p>
                              )}
                              {item.costInPoints > 0 && (
                                <div className="flex justify-center items-center card-footer-cost-cnt">
                                  <span className="text-3xl uppercase nersans font-bold card-footer-cost-points">
                                    {item.costInPoints}
                                  </span>
                                  <span className="text-xs uppercase nersans mt-2 ml-2">{POINTS_NAME}</span>
                                </div>
                              )}
                              {item.costInPoints === 0 && (
                                <div className="flex justify-center items-center card-footer-cost-free">
                                  {textServices.findByText('trivias/list/card/free', { es: 'GRATIS', en: 'FREE' })}
                                </div>
                              )}
                            </div>
                          </DivTriviaContainer>
                          {!hasEnoughPoints && (
                            <div className="uppercase raleway text-xs text-center pb-4 card-footer-cost-not-found">
                              {textServices.findByText('trivias/list/card/enough', {
                                es: 'no tienes suficientes',
                                en: "you don't have enough"
                              })}{' '}
                              {POINTS_NAME}
                            </div>
                          )}
                        </DivTriviaListBorder>
                      </Link>
                    )
                  } else if (isAvailable && item?.stock > 1) {
                    return (
                      <div
                        key={`trivia-item-${item.id}`}
                        className={`h-full trivias__card-list ${isActive || item?.withRanking ? 'trivias__card-list-active' : 'pointer-events-none'}`}
                      >
                        <DivTriviaListBorder
                          className={`border-b card-container h-full flex flex-col`}
                          isActive={isActive}
                        >
                          <DivTitleTriviaList
                            className={` flex justify-start items-center card-title-cnt ${isActive ? '' : 'opacity-80'}`}
                          >
                            <H2TitleTriviaList className="font-bold uppercase text-lg py-4 pl-6 pr-4 card-title-int">
                              {item.interest}
                            </H2TitleTriviaList>
                            <TriviaTitle className="text-sm py-4 pl-4 pr-6 flex justify-center items-center card-title-name">
                              {item?.name}
                            </TriviaTitle>
                          </DivTitleTriviaList>
                          <DivTriviaContainer className="pt-4 flex flex-col justify-between flex-1 card-body">
                            <TriviaSubtitle className="din pb-2 text-center px-6 card-body-subtitle">
                              {textServices.findByText('trivias/questions1', { es: `Contesta `, en: 'Answer ' })}
                              <TriviaSubtitleSpan className="card-body-subtitle-question">
                                {item.nbrOfQuestions}
                              </TriviaSubtitleSpan>
                              {textServices.findByText('trivias/questions2', {
                                es: ` preguntas sobre el tema y gana:`,
                                en: ` questions on the subject and win:`
                              })}
                            </TriviaSubtitle>
                            <TriviaPrize className="text-center uppercase text-2xl px-6 pb-2 card-body-prize">
                              {item.prize}
                            </TriviaPrize>
                            {!isUnlimited && (
                              <TriviaStock className="din uppercase text-center text-xs card-body-stock">
                                {item.stock}{' '}
                                {textServices.findByText('trivias/list/card/available', {
                                  es: 'disponible',
                                  en: 'available'
                                })}
                                {item.stock > 1 ? 's' : ''}
                              </TriviaStock>
                            )}
                            <div className="flex justify-between w-full items-end px-6 mt-4 py-1 card-footer">
                              <div className="card-footer-time-cnt">
                                {shouldShowCountdown && (
                                  <TriviaTime className="card-footer-time-text">
                                    {countdownType}
                                    <br />{' '}
                                    <TriviaTimeNumbers className="card-footer-time-countdown">
                                      <CountdownDays date={dateCountdown} />
                                    </TriviaTimeNumbers>
                                  </TriviaTime>
                                )}
                              </div>
                              {item.costInPoints > 0 && (
                                <TriviaCost className="flex justify-center items-center card-footer-cost-cnt">
                                  <p>
                                    {' '}
                                    <TriviaCostNumber className="card-footer-cost-points">
                                      {item.costInPoints}
                                    </TriviaCostNumber>{' '}
                                    {POINTS_NAME}{' '}
                                  </p>
                                  {/* <span className="text-3xl uppercase nersans font-bold">{item.costInPoints}</span>
                            <span className="text-xs uppercase nersans mt-2 ml-2">{POINTS_NAME}</span> */}
                                </TriviaCost>
                              )}
                              {item.costInPoints === 0 && (
                                <TriviaCost className="flex justify-center items-center card-footer-cost-free">
                                  {textServices.findByText('trivias/list/card/free', { es: 'GRATIS', en: 'FREE' })}
                                </TriviaCost>
                              )}
                            </div>
                          </DivTriviaContainer>
                          {!hasEnoughPoints && (
                            <div className="uppercase din text-xs text-center pb-2 pt-2 card-footer-cost-not-found">
                              {textServices.findByText('trivias/list/card/enough', {
                                es: 'no tienes suficientes',
                                en: "you don't have enough"
                              })}{' '}
                              {POINTS_NAME}
                            </div>
                          )}
                        </DivTriviaListBorder>
                      </div>
                    )
                  } else {
                    return null
                  }
                })}
              </Slider>
            ) : (
              <div className="grid grid-cols-1 md:gap-4 md:grid-cols-2 lg:grid-cols-3 auto-rows-fr w-full trivias__container__cards">
                <AvailableTrivias arrTrivias={avaliableTrivias} />
              </div>
            )
          ) : (
            <TriviaNotFoundContainer className="w-full h-full flex justify-center items-center trivias__not-found-container">
              <TriviaNotFoundText className="font-bold text-3xl text-center trivias__not-found-text">
                {textServices.findByText('trivias/not-found', {
                  es: 'NO HAY TRIVIAS DISPONIBLES',
                  en: 'NO TRIVIA AVAILABLE'
                })}
              </TriviaNotFoundText>
            </TriviaNotFoundContainer>
          )}
        </div>
        {currentItem && (
          <div className="trivias__play-btn">
            <Button onClick={handleOpenTrivia}>Comenzar trivia</Button>
          </div>
        )}
      </DivTriviaList>
    </>
  )
}

export default TriviaList
